import {
  inject,
  Injectable,
  makeStateKey,
  PLATFORM_ID,
  TransferState,
} from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../../environments/environment';
import { Proof } from '../../interfaces/proof.interface';

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  private meta = inject(Meta);
  private titleService = inject(Title);
  private platformId = inject(PLATFORM_ID);
  private router = inject(Router);
  private http = inject(HttpClient);
  private transferState = inject(TransferState);

  constructor() { }

  async resolveAndSetTags(proof: Proof): Promise<string> {
    const { hash, seo_title, seo_description, seo_image } = proof;
    console.log(
      '[SeoService] resolving tags for:',
      hash,
      'seo_image present:',
      !!seo_image,
    );

    let image = seo_image;

    if (!image) {
      image = await this.resolveOgImage(proof);
    }

    image =
      image ||
      `https://assets.simpleproof.com/rich-snippets/v1/og.jpg?v=${Date.now()}`;

    let currentUrl = '';
    if (isPlatformBrowser(this.platformId)) {
      currentUrl = window.location.href;
    } else {
      currentUrl = environment.WEB_URL + this.router.url;
    }

    const title =
      seo_title || `File Proof Verification - ${hash.substring(0, 6)}`;
    const description =
      seo_description ||
      `Blockchain file verification of ${hash.substring(0, 6)} by Simple Proof.`;

    this.titleService.setTitle(title);

    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:url', content: currentUrl });
    this.meta.updateTag({ property: 'og:image', content: image });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({
      property: 'og:site_name',
      content: 'Simple Proof Verification Link',
    });

    this.meta.updateTag({ name: 'description', content: description });

    this.meta.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });
    this.meta.updateTag({ name: 'twitter:title', content: title });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image', content: image });
    this.meta.updateTag({ name: 'twitter:image:alt', content: title });

    return image;
  }

  private async resolveOgImage(proof: Proof): Promise<string> {
    const { srcName, extension, space, prefix, file_attribute, otsName, hash } =
      proof;
    const OG_IMAGE_KEY = makeStateKey<string>(`og-image-${hash}`);

    console.log('[SeoService] resolveOgImage called with:', { space, prefix, hash });
    console.log('[SeoService] Starting OG image resolution...');

    if (this.transferState.hasKey(OG_IMAGE_KEY)) {
      const cachedUrl = this.transferState.get(OG_IMAGE_KEY, '');
      console.log('[SeoService] Using cached OG image:', cachedUrl);
      return cachedUrl;
    }

    console.log('[SeoService] No cached OG image, proceeding with resolution...');

    const baseUrl = 'https://assets.simpleproof.com/rich-snippets/v1/';
    let ext = 'pdf';

    if (!srcName || srcName === '') {
      if (file_attribute?.name === 'FILE_NOT_DOWNLOADABLE') {
        if (otsName) {
          const otsBase = otsName.replace(/^v2-/, '').replace(/\.ots$/, '');
          const parts = otsBase.split('.');
          ext =
            parts.length > 1
              ? parts[parts.length - 1].toLowerCase()
              : 'unknown';
        } else {
          ext = 'unknown';
        }
      } else if (file_attribute?.name === 'HASH_ONLY') {
        ext = 'hash';
      } else {
        ext = 'unknown';
      }
    } else {
      if (extension) {
        ext = extension.toLowerCase();
      } else {
        const parts = srcName.split('.');
        ext = parts.length > 1 ? parts[parts.length - 1].toLowerCase() : 'pdf';
      }
    }

    console.log('[SeoService] OG Image lookup - space:', space, 'prefix:', prefix);

    if (space && prefix != null) {
      const ogImageKey = `${prefix}og_image.webp`;
      console.log('[SeoService] Constructed OG Image key:', ogImageKey);

      try {
        const url = `${environment.CDN_URL}${space}/${prefix}og_image.webp`;
        console.log('[SEO] Testing OG image:', url);

        try {
          await firstValueFrom(
            this.http.head(url, { observe: 'response', responseType: 'text' })
          );
          console.log('[SEO] ✅ OG image found');
          this.transferState.set(OG_IMAGE_KEY, url);
          return url;
        } catch {
          console.log('[SEO] ❌ OG image not found, using fallback');
        }
      } catch (e) {
        console.log('[SeoService] Error constructing CDN URL:', e);
      }
    }

    const imageMap: Record<string, string> = {
      zip: 'og-archive-outline.png',
      rar: 'og-archive-outline.png',
      '7z': 'og-archive-outline.png',
      js: 'og-code-slash-outline.png',
      ts: 'og-code-slash-outline.png',
      py: 'og-code-slash-outline.png',
      java: 'og-code-slash-outline.png',
      cpp: 'og-code-slash-outline.png',
      c: 'og-code-slash-outline.png',
      html: 'og-code-slash-outline.png',
      css: 'og-code-slash-outline.png',
      pdf: 'og-document-outline.png',
      doc: 'og-document-outline.png',
      docx: 'og-document-outline.png',
      txt: 'og-document-text-outline.png',
      md: 'og-document-text-outline.png',
      psd: 'og-easel-outline.png',
      ai: 'og-easel-outline.png',
      xls: 'og-grid-outline.png',
      xlsx: 'og-grid-outline.png',
      csv: 'og-grid-outline.png',
      jpg: 'og-image-outline.png',
      jpeg: 'og-image-outline.png',
      png: 'og-image-outline.png',
      gif: 'og-image-outline.png',
      svg: 'og-image-outline.png',
      mp3: 'og-musical-notes-outline.png',
      wav: 'og-musical-notes-outline.png',
      flac: 'og-musical-notes-outline.png',
      mp4: 'og-videocam-outline.png',
      avi: 'og-videocam-outline.png',
      mov: 'og-videocam-outline.png',
      hash: 'og-vl-bluebg.png',
      unknown: 'og-vl-bluebg.png',
    };

    const defaultUrl = baseUrl + (imageMap[ext] || 'og-vl-bluebg.png');
    this.transferState.set(OG_IMAGE_KEY, defaultUrl);
    return defaultUrl;
  }

  setDefaultTags(): void {
    const currentUrl = 'https://verify.simpleproof.com';

    this.titleService.setTitle('Simple Proof Verification Link');

    this.meta.updateTag({
      property: 'og:title',
      content: 'Simple Proof Verification Link',
    });
    this.meta.updateTag({
      property: 'og:description',
      content:
        'Web platform for verifying blockchain confirmations of uploaded files.',
    });
    this.meta.updateTag({ property: 'og:url', content: currentUrl });
    this.meta.updateTag({
      property: 'og:image',
      content: `https://assets.simpleproof.com/rich-snippets/v1/og.jpg?v=${Date.now()}`,
    });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({
      property: 'og:site_name',
      content: 'Simple Proof Verification Link',
    });

    // Twitter Cards Default
    this.meta.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });
    this.meta.updateTag({
      name: 'twitter:title',
      content: 'Simple Proof Verification Link',
    });
    this.meta.updateTag({
      name: 'twitter:description',
      content:
        'Web platform for verifying blockchain confirmations of uploaded files.',
    });
    this.meta.updateTag({
      name: 'twitter:image',
      content: `https://assets.simpleproof.com/rich-snippets/v1/twitter-vl-bluebg.png?v=${Date.now()}`,
    });
    this.meta.updateTag({
      name: 'twitter:image:alt',
      content: 'Simple Proof Verification Link',
    });
  }
}
